import * as types from './types';
import { uiActions } from 'state/modules/UI';
import {
  fetchUser,
  mfaVerify,
  resendOtp,
  setPassword,
  getBastions,
  refreshToken,
  confirmEmail,
  changePassword,
  login as LoginAction,
  // oAuthLogin as oAuthLoginAction,
  logout as LogoutAction,
  fetchLoginTransferToken,
  forgotPassword as forgotPasswordAction,
  getClients,
  startTwoFactor,
  getTwoFactorConfiguration,
  saveTwoFactorConfiguration,
  createIdentityProvider,
  fetchCurrentIdentityProvider,
  updateIdentityProvider,
  importCertificate,
} from 'services/api/auth';

import {
  changePassword as changePasswordFA,
  twoFactorLogin as twoFactorLoginFA,
} from 'services/api/fusionAuthApi';

import i18n from 'i18n-js';
import moment from 'moment-timezone';
import * as Sentry from '@sentry/browser';
import { userActions } from 'state/modules/user';
import * as usersActions from 'services/api/users';
import { configActions } from 'state/modules/config';
import { getFAErrorMessage, getErrorMessage, clearStorageItems, hasAuthCookie } from 'utils';
import Cookies from 'universal-cookie';

const loginUserRequest = () => ({
  type: types.FETCH_LOGIN_REQUEST,
});

const loginUserSuccess = (payload) => ({
  type: types.FETCH_LOGIN_SUCCESS,
  payload,
});

const loginUserFailed = () => ({
  type: types.FETCH_LOGIN_FAILED,
});

const oAuthLoginUserSuccess = (payload) => ({
  type: types.FETCH_OAUTH_LOGIN_SUCCESS,
  payload,
});

const setLoggedInUser = () => ({
  type: types.SET_LOGGED_IN,
});

const loginMFASuccess = () => ({
  type: types.FETCH_LOGIN_MFA_SUCCESS,
});

const setCredentials = (data) => {
  localStorage.setItem('access_token', data?.auth?.access_token);
  localStorage.setItem('refresh_token', data?.auth?.refresh_token);

  const orgs = data?.to_orgs || [];
  const org = orgs.find((val) => val.is_default);
  sessionStorage.setItem('org_slug', data.org_default || org?.organization?.id_slug);
};

export const postLogin = () => async (dispatch) => {
  const userAuth = localStorage.getItem('access_token');
  if (!userAuth) {
    return dispatch(loginUserFailed());
  }
  dispatch(fetchUserMe());
  return;
};

export const login = (payload) => {
  return async (dispatch) => {
    dispatch(loginUserRequest());

    const response = await LoginAction(payload);

    if (response.ok) {
      if (response?.data?.message === 'OTPREQUIRED') {
        localStorage.setItem('otp_token', response?.data.token);
        return dispatch(loginMFASuccess());
      }

      dispatch(postLoginActions(response?.data));
      return dispatch(postLogin());
    }

    let message = response?.data?.message || '';
    if (response?.data?.key === 'LOCKEDBYSECURITY')
      message =
        'Your account has been disabled by failed attempts to log in. Please reset your password and try again.';

    dispatch(uiActions.handleShowSnackbar({ type: 'error', message }));
    dispatch(loginUserFailed());
  };
};

export const fetchUserRequest = () => ({
  type: types.FETCH_USER_REQUEST,
});

export const fetchUserSuccess = (payload) => ({
  type: types.FETCH_USER_SUCCESS,
  payload,
});

export const fetchUserFailed = () => ({
  type: types.FETCH_USER_FAILED,
});

export const postOauthLogin = () => async (dispatch) => {
  const cookies = new Cookies();

  const userAuth = hasAuthCookie() ? cookies.get('app.idt') : localStorage.getItem('access_token');

  if (!userAuth) {
    return dispatch(loginUserFailed());
  }
  dispatch(fetchUserMe());
  return;
};

export const oAuthLogin = () => {
  return async (dispatch) => {
    dispatch(oAuthLoginUserSuccess());
    return dispatch(postOauthLogin());
  };
};

export const fetchUserUpdated = () => {
  return async (dispatch) => {
    const response = await fetchUser();
    if (response.ok) {
      return dispatch(fetchUserSuccess(response.data));
    }
  };
};

export const fetchUserMe = () => {
  return async (dispatch) => {
    dispatch(fetchUserRequest());

    const response = await fetchUser();

    if (response.ok) {
      const user = response.data;
      dispatch(userActions.fetchUserPrivilegesRequest());
      await dispatch(userActions.fetchUserPrivileges(user.id));
      await dispatch(configActions.fetchConfig());
      dispatch(setLoggedInUser());
      const org = sessionStorage.getItem('org_slug');

      // set timezone to default local
      moment.tz.setDefault(moment.tz.guess());

      Sentry.configureScope((scope) => {
        scope.setUser({ id: user.id });
      });

      if (!org) sessionStorage.setItem('org_slug', user?.organization?.id_slug);

      return dispatch(fetchUserSuccess(response.data));
    }

    if (response.status === 401) {
      if (response?.data?.key === 'ACCESSTOKENEXPIRED') {
        if (!hasAuthCookie()) {
          return dispatch(fetchRefreshToken());
        }
      }
    }
    if (response?.data?.key !== 'PASSWORDEXPIRED') {
      clearStorageItems();
      return dispatch(fetchUserFailed());
    } else {
      return dispatch(setLoggedInUser({}));
    }
  };
};

const fetchRefreshTokenRequest = () => ({
  type: types.FETCH_REFRESH_TOKEN_REQUEST,
});

const fetchRefreshTokenSuccess = (payload) => ({
  type: types.FETCH_REFRESH_TOKEN_SUCCESS,
  payload,
});

const fetchRefreshTokenFailed = (error) => ({
  type: types.FETCH_REFRESH_TOKEN_FAILED,
  error,
});

export const fetchRefreshToken =
  (clean = false) =>
  async (dispatch) => {
    dispatch(fetchRefreshTokenRequest());

    const payload = {
      access_token: localStorage.getItem('refresh_token'),
      enabled: true,
    };
    const response = await refreshToken(payload);

    if (response.ok) {
      localStorage.setItem('access_token', response?.data?.access_token);

      dispatch(fetchRefreshTokenSuccess(response.data));
      if (!clean) {
        dispatch(postLogin());
      }
    }
    if (!clean) {
      dispatch(logout());
      dispatch(fetchRefreshTokenFailed());
    }
  };

export const fetchRefreshTokenClean = () => async (dispatch) => {
  dispatch(fetchRefreshTokenRequest());

  const payload = {
    access_token: localStorage.getItem('refresh_token'),
    enabled: true,
  };
  const response = await refreshToken(payload);

  if (response.ok) {
    localStorage.setItem('access_token', response?.data?.access_token);
    dispatch(fetchRefreshTokenSuccess(response.data));
  }
  dispatch(fetchRefreshTokenFailed());
};

const mfaRequest = () => ({
  type: types.FETCH_MFA_REQUEST,
});

const mfaSuccess = (payload) => ({
  type: types.FETCH_MFA_SUCCESS,
  payload,
});

const mfaFailed = () => ({
  type: types.FETCH_MFA_FAILED,
});

const postLoginActions =
  (data, login = true) =>
  async (dispatch) => {
    setCredentials(data);
    if (login) {
      dispatch(loginUserSuccess(data));
    }
  };

export const verifyOTP = (code) => {
  return async (dispatch) => {
    const token = localStorage.getItem('otp_token');

    if (!token) {
      return dispatch(
        uiActions.handleShowSnackbar({
          type: 'error',
          message: 'Token is not valid. Please login again',
        })
      );
    }

    const payload = { token, otp: code };

    dispatch(mfaRequest());

    const response = await mfaVerify(payload);

    if (response.ok) {
      dispatch(mfaSuccess());
      localStorage.removeItem('otp_token');
      dispatch(postLoginActions(response?.data));
      return dispatch(postLogin());
    }

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: getErrorMessage(response.data),
      })
    );
    dispatch(mfaFailed());
  };
};

const forgotPasswordRequest = () => ({
  type: types.FETCH_FORGOT_PASSWORD_REQUEST,
});

const forgotPasswordSuccess = (payload) => ({
  type: types.FETCH_FORGOT_PASSWORD_SUCCESS,
  payload,
});

const forgotPasswordFailed = () => ({
  type: types.FETCH_FORGOT_PASSWORD_FAILED,
});

export const forgotPassword = (payload) => {
  return async (dispatch) => {
    dispatch(forgotPasswordRequest());

    const response = await forgotPasswordAction(payload);

    if (response.ok) {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: i18n.t('auth.password_reset_email'),
        })
      );
      return dispatch(forgotPasswordSuccess(response.data));
    }

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: getErrorMessage(response.data),
      })
    );
    dispatch(forgotPasswordFailed());
  };
};

const fetchChangePasswordRequest = () => ({
  type: types.FETCH_CHANGE_PASSWORD_REQUEST,
});

const fetchChangePasswordSuccess = () => ({
  type: types.FETCH_CHANGE_PASSWORD_SUCCESS,
});

const fetchChangePasswordFailed = (error) => ({
  type: types.FETCH_CHANGE_PASSWORD_FAILED,
  error,
});

export const fetchChangePassword = (payload, onNext) => {
  return async (dispatch) => {
    dispatch(fetchChangePasswordRequest());
    const response = await changePassword(payload);

    if (response.ok) {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: i18n.t('auth.change_password_successfully'),
        })
      );
      if (onNext) {
        onNext();
      }
      return dispatch(fetchChangePasswordSuccess(response.data));
    }
    const error = getErrorMessage(response.data);

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: error,
      })
    );
    dispatch(fetchChangePasswordFailed(error));
  };
};

export const fetchChangePasswordFA = (payload, onNext) => {
  return async (dispatch) => {
    dispatch(fetchChangePasswordRequest());
    const response = await changePasswordFA(payload);

    if (response.ok) {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: i18n.t('auth.change_password_successfully'),
        })
      );
      if (onNext) {
        onNext();
      }
      return dispatch(fetchChangePasswordSuccess(response.data));
    }
    const error = getFAErrorMessage(response.data);

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: error,
      })
    );
    dispatch(fetchChangePasswordFailed(error));
  };
};

const logoutRequest = () => ({
  type: types.USER_LOGGED_OUT,
});

export const logout = (data) => async (dispatch) => {
  await LogoutAction(data);
  Sentry.configureScope((scope) => {
    scope.clear();
  });
  clearStorageItems();
  return dispatch(logoutRequest());
};

export const restoreUserAccess = () => {
  return async (dispatch) => {
    dispatch(fetchUserMe());
  };
};

export const fetchResetPasswordState = () => ({
  type: types.FETCH_RESET_PASSWORD,
});

const fetchSetPasswordRequest = () => ({
  type: types.FETCH_SET_PASSWORD_REQUEST,
});

const fetchSetPasswordSuccess = (payload) => ({
  type: types.FETCH_SET_PASSWORD_SUCCESS,
  payload,
});

const fetchSetPasswordFailed = (error) => ({
  type: types.FETCH_SET_PASSWORD_FAILED,
  error,
});

export const fetchSetPassword = (token, payload) => async (dispatch) => {
  dispatch(fetchSetPasswordRequest());

  const response = await setPassword(token, payload);

  if (response.ok) {
    return dispatch(fetchSetPasswordSuccess());
  }

  dispatch(
    uiActions.handleShowSnackbar({
      type: 'error',
      message: getErrorMessage(response.data),
    })
  );
  return dispatch(fetchSetPasswordFailed());
};

const fetchBastionsRequest = () => ({
  type: types.FETCH_BASTIONS_REQUEST,
});

const fetchBastionsSuccess = (payload) => ({
  type: types.FETCH_BASTIONS_SUCCESS,
  payload,
});

const fetchBastionsFailed = ({ error, errKey = '' }) => ({
  type: types.FETCH_BASTIONS_FAILED,
  error,
  errKey,
});

export const resetBastions = () => ({
  type: types.FETCH_RESET_BASTIONS,
});

export const setCurrentBastion = (payload) => ({
  type: types.SET_CURRENT_BASTION,
  payload,
});

export const fetchBastions = (email) => async (dispatch) => {
  dispatch(fetchBastionsRequest());

  const response = await getBastions(email);

  if (response.ok) {
    if (response.data && response.data.length) return dispatch(fetchBastionsSuccess(response.data));

    return dispatch(fetchBastionsFailed({ error: 'Email not found' }));
  }

  const errKey = response?.data?.key || '';
  if (errKey !== 'RATELIMITEXCEEDED') {
    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: getErrorMessage(response?.data),
      })
    );
  }
  return dispatch(fetchBastionsFailed({ errKey }));
};

const resendOTPRequest = () => ({
  type: types.RESEND_OTP_REQUEST,
});

const resendOTPSuccess = () => ({
  type: types.RESEND_OTP_SUCCESS,
});

const resendOTPFailed = (payload) => ({
  type: types.RESEND_OTP_FAILED,
  payload,
});

export const resendOTP = () => async (dispatch) => {
  const token = localStorage.getItem('otp_token');
  if (!token) {
    dispatch(
      resendOTPFailed({
        key: 'UNAUTHENTICATED',
      })
    );
    return dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: 'Token is not valid. Please login again',
      })
    );
  }

  dispatch(resendOTPRequest());
  const response = await resendOtp({ token });

  if (response.ok) {
    localStorage.setItem('otp_token', response.data.token);
    dispatch(
      uiActions.handleShowSnackbar({
        type: 'info',
        message: 'The One Time Password has been sent to your email. Please enter the number below',
      })
    );
    return dispatch(resendOTPSuccess());
  }

  dispatch(
    uiActions.handleShowSnackbar({
      type: 'error',
      message: getErrorMessage(response.data),
    })
  );

  return dispatch(resendOTPFailed(response.data));
};

const resetOTPSuccess = () => ({
  type: types.RESET_OTP_SUCCESS,
});

// Reset OTP
export const resetOTP = () => async (dispatch) => {
  dispatch(resetOTPSuccess());
};

// Confirm email
const fetchConfirmEmailRequest = () => ({
  type: types.FETCH_EMAIL_CONFIRM_REQUEST,
});

const fetchConfirmEmailSuccess = () => ({
  type: types.FETCH_EMAIL_CONFIRM_SUCCESS,
});

const fetchConfirmEmailFailed = (error) => ({
  type: types.FETCH_EMAIL_CONFIRM_FAILED,
  error,
});

export const fetchConfirmEmail = (params) => async (dispatch) => {
  dispatch(fetchConfirmEmailRequest());
  const response = await confirmEmail(params);

  if (response.ok) {
    return dispatch(fetchConfirmEmailSuccess());
  }
  const error = getErrorMessage(response.data);
  return dispatch(fetchConfirmEmailFailed(error));
};

// Fetch access token
const fetchAccessTokenRequest = () => ({
  type: types.FETCH_ACCESS_TOKEN_REQUEST,
});

const fetchAccessTokenFailed = (error) => ({
  type: types.FETCH_ACCESS_TOKEN_FAILED,
  error,
});

export const fetchAccessToken = (params, org) => async (dispatch) => {
  dispatch(fetchAccessTokenRequest());
  const response = await fetchLoginTransferToken(params);

  if (response.ok) {
    const data = {
      auth: { ...response?.data },
      org_default: org,
    };
    dispatch(postLoginActions(data, false));
    return dispatch(postLogin());
  }
  const error = getErrorMessage(response.data);
  return dispatch(fetchAccessTokenFailed(error));
};

const fetchUpdateAccountRequest = (payload) => ({
  type: types.FETCH_UPDATE_ACCOUNT_REQUEST,
  payload,
});

const fetchUpdateAccountSuccess = (payload) => ({
  type: types.FETCH_UPDATE_ACCOUNT_SUCCESS,
  payload,
});

const fetchUpdateAccountFailed = () => ({
  type: types.FETCH_UPDATE_ACCOUNT_FAILED,
});

export const resetVerification = () => ({
  type: types.RESET_VERIFICATION_CODE,
});

export const fetchUpdateAccount = (payload, type, onSuccess, message, subtype) => {
  return async (dispatch) => {
    dispatch(fetchUpdateAccountRequest({ type: subtype }));

    let response = {};
    switch (type) {
      case 'email':
        response = await usersActions.fetchEmailConfirm(payload);
        break;
      case 'info': {
        const id = payload.id;
        delete payload.id;
        response = await usersActions.fetchUpdateUser(id, payload);
        break;
      }
      default:
        response = await usersActions.fetchPhoneConfirm(payload);
        break;
    }

    if (response.ok) {
      if (onSuccess) {
        onSuccess();
      }
      if (message) {
        dispatch(
          uiActions.handleShowSnackbar({
            type: message.type,
            message: i18n.t(message.message),
          })
        );
      }
      return dispatch(fetchUpdateAccountSuccess(response.data));
    }

    const error = getErrorMessage(response.data);

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: error,
      })
    );
    dispatch(fetchUpdateAccountFailed(error));
  };
};

export const switchOrg = () => ({
  type: types.SWITCH_ORGANIZATION,
});

export const unauthorized = () => ({
  type: types.FETCH_UNAUTHORIZED,
});

// GET CLIENTS IDs
const fetchClientsRequest = () => ({
  type: types.FETCH_CLIENTS_REQUEST,
});

const fetchClientsSuccess = (payload) => ({
  type: types.FETCH_CLIENTS_SUCCESS,
  payload,
});

const fetchClientsFailed = (error) => ({
  type: types.FETCH_CLIENTS_FAILED,
  error,
});

export const fetchClients = (bastion) => async (dispatch) => {
  dispatch(fetchClientsRequest());

  const response = await getClients(bastion);

  if (response.ok) {
    return dispatch(fetchClientsSuccess(response.data));
  }

  dispatch(
    uiActions.handleShowSnackbar({
      type: 'error',
      message: getErrorMessage(response.data),
    })
  );
  return dispatch(fetchClientsFailed());
};

// START TWO FACTOR
const fetchStartTwoFactorRequest = () => ({
  type: types.FETCH_START_TWO_FACTOR_REQUEST,
});

const fetchStartTwoFactorSuccess = (payload) => ({
  type: types.FETCH_START_TWO_FACTOR_SUCCESS,
  payload,
});

const fetchStartTwoFactorFailed = (error) => ({
  type: types.FETCH_START_TWO_FACTOR_FAILED,
  error,
});

export const fetchStartTwoFactor = () => async (dispatch) => {
  dispatch(fetchStartTwoFactorRequest());
  const response = await startTwoFactor();
  if (response.ok) {
    return dispatch(fetchStartTwoFactorSuccess(response.data));
  }
  dispatch(
    uiActions.handleShowSnackbar({
      type: 'error',
      message: getErrorMessage(response.data),
    })
  );
  return dispatch(fetchStartTwoFactorFailed());
};

// TWO FACTOR LOGIN
const fetchTwoFactorLoginRequest = () => ({
  type: types.FETCH_TWO_FACTOR_LOGIN_REQUEST,
});

const fetchTwoFactorLoginSuccess = (payload) => ({
  type: types.FETCH_TWO_FACTOR_LOGIN_SUCCESS,
  payload,
});

const fetchTwoFactorLoginFailed = (error) => ({
  type: types.FETCH_TWO_FACTOR_LOGIN_FAILED,
  error,
});

export const fetchTwoFactorLogin = (payload) => async (dispatch) => {
  dispatch(fetchTwoFactorLoginRequest());
  const response = await twoFactorLoginFA(payload);
  if (response.ok) {
    return dispatch(fetchTwoFactorLoginSuccess(response.data));
  }
  return dispatch(fetchTwoFactorLoginFailed());
};

// FETCH TWO FACTOR CONFIGURATION
const fetchTwoFactorConfigurationRequest = () => ({
  type: types.FETCH_TWO_FACTOR_CONFIG_REQUEST,
});

const fetchTwoFactorConfigurationSuccess = (payload) => ({
  type: types.FETCH_TWO_FACTOR_CONFIG_SUCCESS,
  payload,
});

const fetchTwoFactorConfigurationFailed = (error) => ({
  type: types.FETCH_TWO_FACTOR_CONFIG_FAILED,
  error,
});

export const fetchTwoFactorConfiguration = () => async (dispatch) => {
  dispatch(fetchTwoFactorConfigurationRequest());
  const response = await getTwoFactorConfiguration();
  if (response.ok) {
    return dispatch(fetchTwoFactorConfigurationSuccess(response.data));
  }
  dispatch(
    uiActions.handleShowSnackbar({
      type: 'error',
      message: getErrorMessage(response.data),
    })
  );
  return dispatch(fetchTwoFactorConfigurationFailed());
};

// SET TWO FACTOR CONFIGURATION
const setTwoFactorConfigurationRequest = () => ({
  type: types.SET_TWO_FACTOR_CONFIG_REQUEST,
});

const setTwoFactorConfigurationSuccess = (payload) => ({
  type: types.SET_TWO_FACTOR_CONFIG_SUCCESS,
  payload,
});

const setTwoFactorConfigurationFailed = (error) => ({
  type: types.SET_TWO_FACTOR_CONFIG_FAILED,
  error,
});

export const setTwoFactorConfiguration = (payload) => async (dispatch) => {
  dispatch(setTwoFactorConfigurationRequest());
  const response = await saveTwoFactorConfiguration(payload);
  if (response.ok) {
    dispatch(
      uiActions.handleShowSnackbar({
        type: 'success',
        message: i18n.t('common.updated_successfully'),
      })
    );
    return dispatch(setTwoFactorConfigurationSuccess(response.data));
  }
  dispatch(
    uiActions.handleShowSnackbar({
      type: 'error',
      message: getErrorMessage(response.data),
    })
  );
  return dispatch(setTwoFactorConfigurationFailed());
};

const onNextPosition = (payload) => ({
  type: types.CHANGE_POSITION,
  payload,
});

const onEnableIdpForm = (payload) => ({
  type: types.ENABLE_IDP_FORM,
  payload,
});

export const changePosition = (type) => (dispatch, getState) => {
  const {
    onboarding: { position },
  } = getState().auth;

  const nextPosition = type === 'next' ? 1 : -1;

  return dispatch(onNextPosition({ position: position + nextPosition }));
};

export const modifyPosition = (position) => (dispatch) => {
  return dispatch(onNextPosition({ position }));
};

export const enableIdpForm = (enable) => (dispatch) => {
  return dispatch(onEnableIdpForm({ enable }));
};

// Get identity provider
const fetchIdentityProviderRequest = () => ({
  type: types.FETCH_IDENTITY_PROVIDER_REQUEST,
});

const fetchIdentityProviderSuccess = (payload, page = 'details') => ({
  type: types.FETCH_IDENTITY_PROVIDER_SUCCESS,
  payload,
  page,
});

const fetchIdentityProviderFailed = () => ({
  type: types.FETCH_IDENTITY_PROVIDER_FAILED,
});

export const fetchIdentityProvider = (page = 'details') => {
  return async (dispatch) => {
    dispatch(fetchIdentityProviderRequest());

    const response = await fetchCurrentIdentityProvider();

    if (response.ok) {
      return dispatch(fetchIdentityProviderSuccess(response.data, page));
    }

    const error = getErrorMessage(response.data);

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: error,
      })
    );

    dispatch(fetchIdentityProviderFailed(error));
  };
};

// Create identity provider
const fetchUpsertIdentityProviderRequest = () => ({
  type: types.FETCH_UPSERT_IDENTITY_PROVIDER_REQUEST,
});

const fetchUpsertIdentityProviderSuccess = (payload) => ({
  type: types.FETCH_UPSERT_IDENTITY_PROVIDER_SUCCESS,
  payload,
});

const fetchUpsertIdentityProviderFailed = (payload) => ({
  type: types.FETCH_UPSERT_IDENTITY_PROVIDER_FAILED,
  payload,
});

export const fetchUpsertIdentityProvider = (payload, step) => async (dispatch, getState) => {
  const {
    onboarding: { steps },
  } = getState().auth;
  dispatch(fetchUpsertIdentityProviderRequest());

  let response;
  if (steps?.details?.id) {
    response = await updateIdentityProvider(payload);
  } else {
    response = await createIdentityProvider(payload);
  }

  if (response.ok) {
    dispatch(
      fetchUpsertIdentityProviderSuccess({
        data: response.data,
        step,
      })
    );
    if (steps?.details?.id) {
      return dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: i18n.t('common.updated_successfully'),
        })
      );
    } else {
      return dispatch(changePosition('next'));
    }
  }

  const formattedError = getErrorMessage(response?.data, {}, true);

  if (typeof formattedError === 'string') {
    dispatch(fetchUpsertIdentityProviderFailed());

    return dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: formattedError,
      })
    );
  }

  let errors = {};

  formattedError?.length &&
    formattedError.map((val) => {
      return (errors[val.field] = val.message);
    });

  dispatch(fetchUpsertIdentityProviderFailed({ errors }));
};

// Import Certificate
const fetchImportCertificateRequest = () => ({
  type: types.FETCH_IMPORT_CERTIFICATE_REQUEST,
});

const fetchImportCertificateSuccess = (payload) => ({
  type: types.FETCH_IMPORT_CERTIFICATE_SUCCESS,
  payload,
});

const fetchImportCertificateFailed = (payload) => ({
  type: types.FETCH_IMPORT_CERTIFICATE_FAILED,
  payload,
});

export const fetchImportCertificate = (payload, step) => async (dispatch, getState) => {
  const {
    onboarding: { steps },
  } = getState().auth;

  dispatch(fetchImportCertificateRequest());

  let response = await importCertificate(payload);

  if (response.ok) {
    if (!steps?.details?.has_certificate) {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: i18n.t('authentication.wizard.import_certificate_success'),
        })
      );
    }
    return dispatch(
      fetchImportCertificateSuccess({
        data: response.data,
        step,
      })
    );
  }

  const formattedError = getErrorMessage(response?.data, {}, true);

  if (typeof formattedError === 'string') {
    dispatch(fetchImportCertificateFailed());

    return dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: formattedError,
      })
    );
  }

  let errors = {};

  formattedError?.length &&
    formattedError.map((val) => {
      return (errors[val.field] = val.message);
    });

  dispatch(fetchImportCertificateFailed({ errors }));
};
